import { Link } from 'react-router-dom';
import { isAdvisorWithoutAdmin } from '../../lib/cashFlowAccess';
import { useCanWrite } from '../../lib/useCanWrite';
import { useWorkspace } from '../../workspace/WorkspaceProvider';
import { useAccessToken } from '../../auth/useAuth';
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceKey } from '../../lib/workspaceQueryKey';
import { createApiClient } from '../../api/client';
import { ArrowLeftRight } from 'lucide-react';
import { levelFromRoles, longMonth, netCents, shortMonth, type InOut, type SummaryMonth, type SummaryResponse } from './cashFlowBoard';
import { dollars } from './Figures';
import { HATCH, NetBarStack, compact, netScaleMax, signed } from './NetBars';
import { questionsQueryOptions } from './questionsQuery';
import { BOARD_MONTHS, useCashFlowBoard } from './useCashFlowBoard';

/**
 * Cash flow on Overview (steward-app#2596, docs/39 §7 decision 11): the Net cash flow card and the
 * Needs attention count. Pieces of the cash flow boards are reused, never copied: the viewer's
 * level (`levelFromRoles`), the summary read (`useCashFlowBoard`, so the card and the board share
 * ONE cache entry), the dollar format and the questions read.
 *
 * ## Who sees anything
 *
 * Only a viewer whose level is Totals by category or Every transaction, and never a firm advisor
 * standing in the family's workspace (`isAdvisorWithoutAdmin`): a firm sees nothing new on a
 * family's Overview. {@link NetCashFlowCard} decides that BEFORE mounting the body, so a viewer at
 * Nothing issues no cash flow read at all from Overview. Everything else renders nothing, with no
 * error and no empty card: cash flow off, nothing computed yet, a refusal, a failed load. The
 * board itself is where those states are explained. UX only: the façade and appcore enforce.
 */

/** Whether this viewer may see cash flow figures on Overview at all. */
export function useOverviewLevel() {
  const { activeMembership } = useWorkspace();
  const roleIds = activeMembership?.roleIds ?? [];
  if (!activeMembership || isAdvisorWithoutAdmin(roleIds)) return 'none' as const;
  return levelFromRoles(roleIds);
}

export function NetCashFlowCard() {
  if (useOverviewLevel() === 'none') return null;
  return <NetCashFlowBody />;
}

/** Overview reads the summary again on mount when the cached one is older than this (the app default is 30s). */
export const OVERVIEW_STALE_MS = 5000;

function NetCashFlowBody() {
  // A board answer or a correction queues a recompute that lands after the write returns, so a
  // summary cached a moment ago may predate it. Once, on mount: if the cached summary is older
  // than OVERVIEW_STALE_MS, read it again. (Younger than the app's 30s staleTime would otherwise
  // be served as is.) Not a poll, and no change to useCashFlowBoard, which the boards share.
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspace();
  useEffect(() => {
    const queryKey = workspaceKey('cash-flow-summary', activeWorkspaceId, 'all', BOARD_MONTHS);
    const st = queryClient.getQueryState(queryKey);
    if (st && st.fetchStatus === 'idle' && st.dataUpdatedAt > 0 && Date.now() - st.dataUpdatedAt > OVERVIEW_STALE_MS) {
      void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount
  }, []);
  const state = useCashFlowBoard<SummaryResponse>('cash-flow-summary', '/cash-flow/summary', 'all');
  if (state.kind !== 'ready' || state.data.status !== 'on') return null;
  const view = overviewView(state.data.months, state.data.to);
  if (!view) return null;
  const { total, span, bars, partial } = view;
  // The same card as Net Worth and Asset Allocation beside it: eyebrow + icon, a stretched link
  // on the eyebrow (keyboard-operable, and no link wrapped around the chart), the hero's figure.
  return (
    <div
      data-testid="net-cash-flow-card"
      className="relative mb-10 grid grid-cols-1 gap-6 rounded-token border border-line bg-surface p-6 sm:p-7 shadow-panel transition-[box-shadow] duration-150 hover:shadow-panel-hover lg:grid-cols-[minmax(0,1fr)_minmax(0,1.7fr)] lg:gap-12"
    >
      <div className="flex flex-col">
        <div className="mb-2 flex items-center gap-2">
          <Link to="/cash-flow" className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate font-sans after:content-[''] after:absolute after:inset-0 after:rounded-token no-underline focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-bronze-strong focus-visible:after:ring-offset-2">Net Cash Flow</Link>
          <ArrowLeftRight size={14} strokeWidth={1.75} aria-hidden className="text-bronze opacity-70" />
        </div>
        <div data-testid="net-cash-flow-total" className="font-serif text-[34px] sm:text-[44px] font-normal leading-none text-navy tracking-[-0.01em]">
          {signed(netCents(total))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-sans font-medium text-slate">
          <span>Inflows {dollars(total.inflowCents)} · Outflows {dollars(total.outflowCents)}</span>
          <span>{span}</span>
        </div>
      </div>
      {bars.length >= 2 && <MonthBars bars={bars} partial={partial} />}
    </div>
  );
}

/** One chart column. `partial`: the month in progress, drawn hatched and kept out of the total. */
interface Bar { month: string; netCents: number; partial: boolean }

/**
 * What the Overview card shows, from the board summary (steward-app#2827).
 *
 * `current` is the summary's `to`: the month the façade's handler calls current (UTC), so the card
 * takes the server's view of which month is in progress, not the browser's clock. (The board does
 * not classify a partial month at all: `defaultMonth` still opens on the newest one with a
 * transaction, which can be the month in progress.) The total
 * counts COMPLETED computed months only. A month a few days old is mostly bills: on Matt's Overview
 * on 2026-11-04, four days of November were counted in the total and shown as "Latest month". The
 * month in progress is drawn as a hatched "so far" bar instead, when it holds a transaction. Only
 * when no completed month holds one does the month in progress become the total, named "so far".
 *
 * Null (the card renders nothing) when no computed month holds a transaction. A computed month
 * with `empty` null (a row from before the count existed) counts as holding one.
 */
export function overviewView(months: readonly SummaryMonth[], current: string): {
  total: InOut; span: string; bars: Bar[]; partial: boolean;
} | null {
  const computed = months.filter((m): m is Extract<SummaryMonth, { computed: true }> => m.computed);
  const complete = computed.filter(m => m.month < current);
  const now = computed.find(m => m.month === current && m.empty !== true);
  const sum = (ms: readonly InOut[]): InOut => ({
    inflowCents: ms.reduce((n, m) => n + m.inflowCents, 0),
    outflowCents: ms.reduce((n, m) => n + m.outflowCents, 0),
  });
  const bars: Bar[] = [
    ...complete.map(m => ({ month: m.month, netCents: netCents(m), partial: false })),
    ...(now ? [{ month: now.month, netCents: netCents(now), partial: true }] : []),
  ];
  if (complete.some(m => m.empty !== true)) {
    const span = complete.length === 1 ? longMonth(complete[0]!.month) : `past ${complete.length} months`;
    return { total: sum(complete), span, bars, partial: !!now };
  }
  if (now) return { total: sum([now]), span: `${longMonth(now.month)} so far`, bars, partial: true };
  return null;
}

/**
 * Net cash flow per month above and below a zero line. Bronze for a month that came out ahead,
 * slate for one that did not, the same pairing as the Net Worth chip (never red); the side of the
 * line says it too, so colour is never the only cue. Every column carries its month and figure as
 * text, because a hover title cannot be reached on touch (steward-app#1028). Below `sm` only the
 * newest six columns show; the accessible label always names every month.
 */
function MonthBars({ bars, partial }: { bars: readonly Bar[]; partial: boolean }) {
  const max = netScaleMax(bars.map(b => b.netCents));
  const label = `Net cash flow by month: ${bars.map(b => `${longMonth(b.month)}${b.partial ? ' so far' : ''} ${signed(b.netCents)}`).join(', ')}`;
  return (
    <figure role="img" aria-label={label} data-testid="net-cash-flow-chart" className="m-0 flex flex-col self-center">
      <div className="flex" aria-hidden>
        {bars.map((b, i) => {
          return (
            <div
              key={b.month}
              data-testid="net-cash-flow-bar"
              data-partial={b.partial || undefined}
              className={`flex min-w-0 flex-1 flex-col items-center ${i < bars.length - 6 ? 'max-sm:hidden' : ''}`}
            >
              <NetBarStack netCents={b.netCents} max={max} partial={b.partial} />
              <div className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.08em] text-slate font-sans">{shortMonth(b.month)}</div>
              <div className={`mt-0.5 text-[11px] sm:text-[12px] font-sans ${b.partial ? 'text-slate' : 'text-body'}`}>{compact(b.netCents)}</div>
            </div>
          );
        })}
      </div>
      {partial && (
        <div className="mt-3 flex items-center justify-end gap-2 text-[11px] font-sans text-slate" aria-hidden>
          <span className="inline-block h-3 w-3 border border-dashed border-current" style={HATCH} />
          This month so far
        </div>
      )}
    </figure>
  );
}

/**
 * The open Worth a look count for the Needs attention row, or 0. Only for a viewer who sees every
 * transaction AND can answer (a read-only member would see a row they cannot act on). Reads the
 * same questions entry as Worth a look. `truncated` means "at least this many".
 */
export function useCashFlowQuestions(): { count: number; atLeast: boolean } {
  const token = useAccessToken();
  const { activeWorkspaceId } = useWorkspace();
  const level = useOverviewLevel();
  const canWrite = useCanWrite();
  const q = useQuery(questionsQueryOptions(createApiClient(token!), activeWorkspaceId,
    !!token && !!activeWorkspaceId && level === 'every' && canWrite));
  if (q.data?.status !== 'on') return { count: 0, atLeast: false };
  return { count: q.data.items.length, atLeast: q.data.truncated === true };
}
